import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import AnimatedSection from './AnimatedSection';
import { MOCK_OFFERINGS } from '../data/mockOfferings';

interface Offering {
  id: string;
  name: string;
  slug: string;
  tagline: string | null;
  description: string;
  category: string;
  heroImageUrl: string | null;
}

const FALLBACK_IMAGES: Record<string, string> = {
  ACTIVATION: '/images/jurassic-jack-dino-experience-009.jpeg',
  ROAMING: '/images/jurassic-dinosaur-jeep-performance-050.jpeg',
  ADD_ON: '/images/jurassic-jack-dino-show-055.png',
};

const OfferingsSection = () => {
  const { data: offerings } = useApi<Offering[]>('/offerings');

  const displayOfferings = (offerings && offerings.length > 0 ? offerings : MOCK_OFFERINGS).slice(0, 6);

  return (
    <section className="relative py-20 bg-charcoal">
      {/* Claw divider */}
      <div className="claw-divider absolute -top-4 left-0 right-0" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-14">
          <p className="font-mono text-sm uppercase tracking-[0.2em] text-amber mb-2">
            Signature Experiences
          </p>
          <h2 className="text-4xl md:text-5xl font-display text-cream">
            Signature Experiences That Scale From{' '}
            <span className="text-orange-vibrant">Schools To Showgrounds</span>
          </h2>
          <p className="mt-4 text-cream/60 max-w-2xl mx-auto">
            Jurassic Jack is designed to pull people in. Kids participate, families follow, crowds gather — and your event gains an attraction that feels alive across the day.
          </p>
        </AnimatedSection>

        {displayOfferings.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayOfferings.map((offering, i) => (
              <AnimatedSection key={offering.id} delay={i * 0.1}>
                <div className="group relative overflow-hidden bg-[#3a3630] rounded-xl shadow-md border border-[#555046] text-white hover:shadow-[0_0_15px_2px_rgba(255,191,0,0.35)] hover:border-[#FFBF00]/40 transition-all duration-300">
                  <div className="aspect-[16/10] overflow-hidden relative">
                    <img
                      src={offering.heroImageUrl || FALLBACK_IMAGES[offering.category] || FALLBACK_IMAGES.ACTIVATION}
                      alt={offering.name}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5 bg-[#3a3630]">
                    <span className="badge-amber text-[10px] mb-2">
                      {offering.category.replace('_', ' ')}
                    </span>
                    <h3 className="font-display text-xl text-white mt-2 font-bold drop-shadow-sm">{offering.name}</h3>
                    {offering.tagline && (
                      <p className="text-sm text-amber mt-1 font-semibold">{offering.tagline}</p>
                    )}
                    <p className="text-sm text-white/95 mt-2 line-clamp-3 leading-relaxed font-medium">{offering.description}</p>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-1 mt-4 text-sm font-display uppercase tracking-wider
                                 text-orange-vibrant hover:text-amber transition-colors font-bold"
                    >
                      Request a Quote
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <div key={n} className="card-dark animate-pulse">
                <div className="aspect-[16/10] bg-charcoal-600" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-charcoal-600 rounded w-1/3" />
                  <div className="h-6 bg-charcoal-600 rounded w-2/3" />
                  <div className="h-4 bg-charcoal-600 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        )}

        <AnimatedSection className="text-center mt-12">
          <Link to="/packages" className="btn-secondary">
            View Packages
          </Link>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default OfferingsSection;
